import {
  Activity,
  Bot,
  Braces,
  Briefcase,
  Building2,
  Calendar,
  CalendarCheck,
  ChartBar,
  ClipboardCheck,
  Database,
  DollarSign,
  Download,
  FileText,
  Flag,
  FlaskConical,
  FolderOpen,
  Gauge,
  GitCompare,
  Hash,
  History,
  Inbox,
  Layers,
  LayoutDashboard,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  type LucideIcon,
  Mail,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  Signal,
  Sparkles,
  Table,
  Tag,
  TrendingUp,
  Upload,
  User,
  UserMinus,
  UserPlus,
  Users,
  Webhook,
  Wrench,
  Zap,
} from 'lucide-react';
import { NON_GRANTABLE_EMPLOYEE_PAGE_SLUGS } from '@/code/lib/employeePageAccess';
import { LinkRouter } from '@/code/lib/routes';
import type { PageGroup } from '../cmd-k/types';

/**
 * Single source of truth for every navigable /employee page.
 * Both the EmployeeLayout navbar and the employee Cmd+K palette derive from
 * this registry, so adding an entry here surfaces the page in both.
 * A drift test (__tests__/employeePageRegistry.test.ts) asserts this list
 * matches the page files under src/pages/employee.
 */

export const CMDK_GROUP_ORDER = [
  'Core',
  'Content',
  'Admin',
  'Messaging & Evals',
  'Dashboards',
  'Utils',
] as const;
export type CmdKGroup = (typeof CMDK_GROUP_ORDER)[number];

export interface EmployeePageEntry {
  /** Path under /employee. 'index' = /employee itself; may contain one slash (e.g. 'utils/leadgen-runs'). */
  slug: string;
  /** Overrides fileNameToLabel(slug) in BOTH the navbar and Cmd+K. */
  label?: string;
  /** Shown in Cmd+K only; the navbar ignores icons. */
  icon: LucideIcon;
  /** Extra Cmd+K search terms beyond the label. */
  keywords?: string[];
  /** Cmd+K group heading. */
  group: CmdKGroup;
  /**
   * Present = top-level navbar link at this left-to-right position (folders
   * occupy positions too — see NAV_FOLDERS). Absent = More dropdown, or a
   * folder dropdown when the slug is under a NAV_FOLDERS prefix.
   * Responsive items are hidden at narrow widths and also kept in the More
   * dropdown as a fallback.
   */
  nav?: { priority: number; responsiveClass?: string };
}

/** Folder dropdowns in the navbar; members = entries whose slug starts with `${folder}/`. */
export const NAV_FOLDERS = [
  { folder: 'dashboards', priority: 12 },
  { folder: 'utils', priority: 13 },
] as const;

// Declaration order matters: it is the Cmd+K within-group order and the
// navbar folder-dropdown order.
export const EMPLOYEE_PAGES: readonly EmployeePageEntry[] = [
  // ── Core ──
  {
    slug: 'index',
    label: 'Dashboard',
    icon: LayoutDashboard,
    keywords: ['home'],
    group: 'Core',
  },
  { slug: 'campaigns', icon: List, group: 'Core', nav: { priority: 1 } },
  {
    slug: 'campaign-bases',
    icon: Layers,
    group: 'Core',
    nav: { priority: 2, responsiveClass: 'hidden lg:inline-flex' },
  },
  { slug: 'leads', icon: UserPlus, group: 'Core', nav: { priority: 3 } },
  {
    slug: 'messaging-inbox',
    icon: Inbox,
    keywords: ['messages'],
    group: 'Core',
    nav: { priority: 4 },
  },
  {
    slug: 'bumping-inbox',
    icon: Zap,
    keywords: ['followup'],
    group: 'Core',
    nav: { priority: 5, responsiveClass: 'hidden lg:inline-flex' },
  },
  { slug: 'customers', icon: Users, group: 'Core', nav: { priority: 6 } },
  {
    slug: 'campaign-conversations',
    icon: MessageCircle,
    group: 'Core',
    nav: { priority: 7, responsiveClass: 'hidden 2xl:inline-flex' },
  },
  {
    slug: 'conversations',
    label: 'Conversation Viewer',
    icon: MessagesSquare,
    keywords: ['conversations', 'bulk', 'transcripts', 'leads', 'read', 'view'],
    group: 'Core',
  },

  // ── Content ──
  {
    slug: 'help-center',
    label: 'Help Center',
    icon: FileText,
    group: 'Content',
    keywords: ['notion', 'help', 'docs', 'faq', 'guides'],
  },

  {
    slug: 'postManager',
    icon: FileText,
    keywords: ['posts'],
    group: 'Content',
    nav: { priority: 8, responsiveClass: 'hidden xl:inline-flex' },
  },
  {
    slug: 'leadpool',
    label: 'Lead Pool',
    icon: FolderOpen,
    keywords: ['leads'],
    group: 'Content',
    nav: { priority: 9, responsiveClass: 'hidden xl:inline-flex' },
  },
  {
    slug: 'company-lists',
    icon: ListChecks,
    keywords: ['inclusion', 'exclusion', 'enrichment', 'icp', 'companies'],
    group: 'Content',
  },

  // ── Admin ──
  {
    slug: 'feature-flags',
    icon: Flag,
    group: 'Admin',
    nav: { priority: 10, responsiveClass: 'hidden 2xl:inline-flex' },
  },
  { slug: 'admin-access', icon: ShieldCheck, group: 'Admin' },
  {
    slug: 'calendar-bookings',
    icon: CalendarCheck,
    keywords: ['calendly', 'cal.com', 'booking', 'meeting', 'match', 'call'],
    group: 'Admin',
  },
  { slug: 'audit-inbox', icon: Search, group: 'Admin' },
  { slug: 'users', icon: User, group: 'Admin' },
  { slug: 'signals', icon: Signal, group: 'Admin' },
  { slug: 'file-manager', icon: FolderOpen, group: 'Admin' },
  { slug: 'onboarding-admin', icon: Briefcase, group: 'Admin' },
  {
    slug: 'emails',
    label: 'Email Templates',
    icon: Mail,
    keywords: ['email', 'templates', 'test'],
    group: 'Admin',
  },
  {
    slug: 'webhooks',
    icon: Webhook,
    group: 'Admin',
    nav: { priority: 11, responsiveClass: 'hidden xl:inline-flex' },
  },
  { slug: 'slack-channels', icon: Hash, group: 'Admin' },
  {
    slug: 'slack-poster',
    icon: Send,
    keywords: ['slack', 'post', 'message', 'bot', 'channel'],
    group: 'Admin',
  },
  {
    slug: 'external-slack-inbox',
    icon: MessageCircle,
    label: 'External Slack Inbox',
    keywords: ['slack', 'inbox', 'customer', 'dm', 'channel', 'message'],
    group: 'Admin',
  },

  // ── Messaging & Evals ──
  { slug: 'individual-chat', icon: MessageSquare, group: 'Messaging & Evals' },
  {
    slug: 'response_evaluation',
    icon: Sparkles,
    group: 'Messaging & Evals',
  },
  {
    slug: 'response-gen-eval',
    icon: FlaskConical,
    keywords: ['evaluation'],
    group: 'Messaging & Evals',
  },
  { slug: 'lead-simulator', icon: Bot, group: 'Messaging & Evals' },
  {
    slug: 'compare-messaging-strategies',
    icon: GitCompare,
    group: 'Messaging & Evals',
  },
  { slug: 'drip-message-tagging', icon: Tag, group: 'Messaging & Evals' },
  {
    slug: 'drip-messaging-analytics',
    icon: TrendingUp,
    group: 'Messaging & Evals',
  },
  { slug: 'iteration-timeline', icon: History, group: 'Messaging & Evals' },
  { slug: 'cleaned-variables', icon: Braces, group: 'Messaging & Evals' },
  {
    slug: 'readout',
    label: 'Readout',
    icon: Table,
    keywords: [
      'readout',
      'crosstab',
      'pivot',
      'matrix',
      'analytics',
      'segment',
      'approach',
      'rep',
      'slack',
      'funnel',
    ],
    group: 'Messaging & Evals',
  },

  // ── Dashboards ── (folder entries in navbar dropdown order)
  {
    slug: 'dashboards/performance-debugger',
    label: 'Performance debugger',
    icon: ChartBar,
    group: 'Dashboards',
    keywords: ['analytics', 'cohorts', 'campaign base', 'funnel'],
  },
  {
    slug: 'dashboards/bump-analytics',
    label: 'Reply, Bump & Booking',
    icon: TrendingUp,
    group: 'Dashboards',
    keywords: [
      'bump',
      'reply',
      'booked',
      'call',
      'connection',
      'approach',
      'audit',
    ],
  },
  {
    slug: 'dashboards/reignition-analytics',
    label: 'Reignition outcomes',
    icon: RotateCcw,
    group: 'Dashboards',
    keywords: ['reignition', 'withdraw', 'second invite', 'outcomes', 'batch'],
  },
  {
    slug: 'dashboards/bump-candidates',
    label: 'Bump Candidates',
    icon: Sparkles,
    group: 'Dashboards',
    keywords: [
      'bump',
      'candidates',
      'reply',
      'nudge',
      'booked',
      'call',
      'audit',
      'generate',
      'response',
    ],
  },
  { slug: 'dashboards/cache-status', icon: Database, group: 'Dashboards' },
  {
    slug: 'dashboards/campaign-leadgen-health',
    icon: ChartBar,
    group: 'Dashboards',
  },
  {
    slug: 'dashboards/campaign-performance',
    icon: Activity,
    group: 'Dashboards',
  },
  {
    slug: 'dashboards/low-outreach-users',
    label: 'Customer Outreach Check',
    icon: LayoutDashboard,
    group: 'Dashboards',
    keywords: [
      'outreach cadence',
      'queued leads',
      'lead dashboard',
      'low outreach users',
    ],
  },
  {
    slug: 'dashboards/linkedin-account-health',
    label: 'LinkedIn Account Health',
    icon: ChartBar,
    group: 'Dashboards',
  },
  { slug: 'dashboards/usage-tracker', icon: Gauge, group: 'Dashboards' },
  {
    slug: 'weekly-stats',
    icon: Calendar,
    keywords: ['stats', 'report'],
    group: 'Dashboards',
  },

  // ── Utils ── (folder entries in navbar dropdown order)
  {
    slug: 'utils/lead-export',
    label: 'Org Lead Export',
    icon: Download,
    keywords: [
      'meetings',
      'calls',
      'csv',
      'export',
      'email',
      'domain',
      'leads',
      'location',
    ],
    group: 'Utils',
  },
  { slug: 'utils/campaign-base-leads', icon: Table, group: 'Utils' },
  {
    slug: 'utils/lead-reignition',
    label: 'Lead Reignition',
    icon: RotateCcw,
    keywords: ['reignition', 'withdraw', 'exit', 'campaign base'],
    group: 'Utils',
  },
  {
    slug: 'utils/curation-queue',
    label: 'Work Queue',
    icon: ListOrdered,
    keywords: [
      'work',
      'curation',
      'queue',
      'ranking',
      'enrichment',
      'search',
      'replenish',
    ],
    group: 'Utils',
  },
  { slug: 'utils/clean-sales-nav-url', icon: Link2, group: 'Utils' },
  { slug: 'utils/company-list-inspector', icon: Building2, group: 'Utils' },
  { slug: 'utils/exit-leads-by-company', icon: UserMinus, group: 'Utils' },
  {
    slug: 'utils/export-linkedin-search',
    label: 'Export LinkedIn Search',
    icon: Upload,
    group: 'Utils',
  },
  {
    slug: 'utils/linkedin-feature-access-check',
    label: 'LinkedIn Feature Access Check',
    icon: ShieldCheck,
    group: 'Utils',
  },
  {
    slug: 'utils/lead-update-audit',
    icon: ClipboardCheck,
    keywords: ['leadgen', 'audit', 'lead update', 'quality'],
    group: 'Utils',
  },
  { slug: 'utils/leadgen-runs', icon: Zap, group: 'Utils' },
  {
    slug: 'utils/segmented-search-plan',
    icon: ListOrdered,
    keywords: ['scheduler', 'queue', 'priority', 'deficit'],
    group: 'Utils',
  },
  { slug: 'utils/pricing-sheet', icon: DollarSign, group: 'Utils' },
  { slug: 'utils/ranking-eval', icon: ListOrdered, group: 'Utils' },
  { slug: 'utils/search-utils', icon: Search, group: 'Utils' },
  { slug: 'utils/usage-tester', icon: Wrench, group: 'Utils' },
  {
    slug: 'utils/mixrank-tester',
    label: 'MixRank Tester',
    icon: Database,
    keywords: ['mixrank', 'enrichment', 'api', 'linkedin', 'email'],
    group: 'Utils',
  },
  {
    slug: 'utils/scheduling-link-test',
    label: 'Scheduling Link Test',
    icon: FlaskConical,
    keywords: [
      'availability',
      'browserbase',
      'booking',
      'cal.com',
      'calendly',
      'hubspot',
      'scheduling',
      'call',
    ],
    group: 'Utils',
  },
  { slug: 'demo-data-generator', icon: Database, group: 'Utils' },
];

// Convert file names to human-readable labels
export function fileNameToLabel(fileName: string): string {
  return fileName
    .replace(/[-_]/g, ' ') // Replace hyphens/underscores with spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
    .replace(/\b\w/g, (c) => c.toUpperCase()); // Title case
}

export const GRANTABLE_EMPLOYEE_PAGES: readonly EmployeePageEntry[] =
  EMPLOYEE_PAGES.filter(
    (page) =>
      !(NON_GRANTABLE_EMPLOYEE_PAGE_SLUGS as readonly string[]).includes(
        page.slug,
      ),
  );

export function getNavLink(entry: EmployeePageEntry): {
  href: string;
  label: string;
} {
  const href = LinkRouter.routeForEmployeePage(entry.slug);
  const lastPart = entry.slug.split('/').pop() || entry.slug;
  return { href, label: entry.label ?? fileNameToLabel(lastPart) };
}

export type NavLink = {
  type: 'link';
  href: string;
  label: string;
  responsiveClass?: string;
};
export type NavDropdown = {
  type: 'dropdown';
  label: string;
  items: { href: string; label: string }[];
};
export type NavItem = NavLink | NavDropdown;

export function buildEmployeeNavItems(
  isAllowed: (slug: string) => boolean = () => true,
): {
  priorityItems: NavItem[];
  moreItems: { href: string; label: string }[];
} {
  const visiblePages = EMPLOYEE_PAGES.filter((entry) => isAllowed(entry.slug));
  const coveredHrefs = new Set<string>();

  const links: { priority: number; item: NavItem }[] = visiblePages
    .filter((entry) => entry.nav)
    .map((entry) => {
      const navLink = getNavLink(entry);
      const responsiveClass = entry.nav?.responsiveClass;
      if (!responsiveClass) {
        coveredHrefs.add(navLink.href);
      }
      return {
        priority: entry.nav?.priority ?? 0,
        item: { type: 'link' as const, ...navLink, responsiveClass },
      };
    });

  const folders: { priority: number; item: NavItem }[] = NAV_FOLDERS.flatMap(
    ({ folder, priority }) => {
      const dropdownItems = visiblePages
        .filter((entry) => entry.slug.startsWith(`${folder}/`))
        .map((entry) => {
          const navLink = getNavLink(entry);
          coveredHrefs.add(navLink.href);
          return navLink;
        });
      if (dropdownItems.length === 0) {
        return [];
      }
      return [
        {
          priority,
          item: {
            type: 'dropdown' as const,
            label: fileNameToLabel(folder),
            items: dropdownItems,
          } satisfies NavItem,
        },
      ];
    },
  );

  const priorityItems = [...links, ...folders]
    .sort((a, b) => a.priority - b.priority)
    .map(({ item }) => item);

  const moreItems = visiblePages
    .map((entry) => getNavLink(entry))
    .filter((item) => !coveredHrefs.has(item.href))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { priorityItems, moreItems };
}

export function filterEmployeePageGroups(
  isAllowed: (slug: string) => boolean = () => true,
): PageGroup[] {
  return CMDK_GROUP_ORDER.map((heading) => ({
    heading,
    items: EMPLOYEE_PAGES.filter(
      (entry) => entry.group === heading && isAllowed(entry.slug),
    ).map((entry) => {
      const { href, label } = getNavLink(entry);
      return { label, href, icon: entry.icon, keywords: entry.keywords };
    }),
  })).filter((group) => group.items.length > 0);
}

/** Cmd+K page groups, derived from the registry in CMDK_GROUP_ORDER. */
export const EMPLOYEE_PAGE_GROUPS: PageGroup[] = filterEmployeePageGroups();
